/**
 * Phase 106 (Plans 106-08, 106-12) — Superadmin Fleet Console dashboard.
 *
 * D-02 locks this surface at `/admin/fleet` inside `topmonks-internal`; D-08
 * requires `topmonks-internal` itself to render as an inventory row with no
 * toggle offered. The frontend `SuperadminRoute` gate is UX only — the
 * backend's `requireSuperadmin` on every `/api/fleet/*` route is the real
 * enforcement boundary (already proven by the backend integration suites).
 *
 * Plan 106-08 shipped this page READ-ONLY (P-29). Plan 106-12 adds the
 * per-row module editor (P-46) — rendered ONLY when `instance.editable` is
 * true; a `managed: external` row (`topmonks-internal`) gets NO action
 * control at all, not even a greyed-out one (same reasoning as P-29: a
 * disabled-looking control on that row would read as "not implemented"
 * rather than "read-only by design") — and the superadmin grant panel
 * (D-09) below the instance table.
 *
 * T-03 (mirrors CronJobsPage.tsx): every external string (rollout reasons,
 * unavailable reasons, manifest errors, error messages, and — as of Plan
 * 106-12 — every validation/save-outcome string) renders as a JSX text node
 * only — React auto-escapes it. Never inject via a raw-HTML assignment prop.
 */
import { useState } from 'react';
import { useFleetInstances } from '../../hooks/useFleetInstances';
import { Spinner } from '../../components/common/Spinner';
import { FleetModuleEditor } from '../../components/admin/FleetModuleEditor';
import { SuperadminGrantPanel } from '../../components/admin/SuperadminGrantPanel';
import type { FleetInstanceView } from '../../services/api/fleet';

function ModuleChip({ id, autoEnabled }: { id: string; autoEnabled: boolean }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 mr-1 mb-1 rounded text-xs font-medium ${
        autoEnabled
          ? 'bg-amber-100 text-amber-800 border border-amber-300'
          : 'bg-gray-100 text-gray-800'
      }`}
      title={autoEnabled ? `${id} — dependency-auto-enabled` : undefined}
      aria-label={autoEnabled ? `${id}, dependency-auto-enabled` : id}
    >
      {id}
    </span>
  );
}

function DesiredModulesCell({ instance }: { instance: FleetInstanceView }) {
  const autoEnabled = new Set(instance.desiredAutoEnabled);
  return (
    <div>
      <div>
        {instance.desiredModules.map((id) => (
          <ModuleChip key={id} id={id} autoEnabled={autoEnabled.has(id)} />
        ))}
      </div>
      {instance.desiredAutoEnabled.length > 0 && (
        <p className="text-xs text-amber-700 mt-0.5">
          Dependency-auto-enabled: {instance.desiredAutoEnabled.join(', ')}
        </p>
      )}
      {instance.desiredError && (
        <p className="text-xs text-red-700 mt-0.5" role="alert">
          Manifest error: {instance.desiredError}
        </p>
      )}
    </div>
  );
}

function RunningModulesCell({ instance }: { instance: FleetInstanceView }) {
  if (!instance.runtimeAvailable) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  if (instance.runningRaw === null) {
    // P-24 — an absent ENABLED_MODULES means "every module", never "no
    // modules". Rendering thirteen chips here would bury that meaning;
    // an explicit label states it instead.
    return (
      <span className="text-xs text-gray-700 italic">
        all modules (no ENABLED_MODULES set)
      </span>
    );
  }
  return (
    <div>
      {instance.runningModules.map((id) => (
        <ModuleChip key={id} id={id} autoEnabled={false} />
      ))}
    </div>
  );
}

/**
 * Drift is a TRUE tri-state, never a boolean. `null` means UNKNOWN (the ECS
 * read failed, `runtimeAvailable: false`) — it must render distinctly from
 * both `true` and `false`, and MUST NOT look like "in sync". Conflating
 * unknown with in-sync is the exact quiet lie this column exists to
 * prevent: it would tell a superadmin a live customer instance's running
 * state matches its desired state when we simply never checked.
 */
function DriftCell({ instance }: { instance: FleetInstanceView }) {
  if (instance.drift === null) {
    return (
      <div>
        <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 text-gray-800 border border-gray-400">
          unknown
        </span>
        {instance.runtimeUnavailableReason && (
          <p className="text-xs text-gray-600 mt-0.5">{instance.runtimeUnavailableReason}</p>
        )}
      </div>
    );
  }
  if (instance.drift === false) {
    return (
      <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
        in sync
      </span>
    );
  }
  return (
    <div>
      <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800">
        drift
      </span>
      {instance.driftDetail.onlyDesired.length > 0 && (
        <p className="text-xs text-red-700 mt-0.5">
          Desired only: {instance.driftDetail.onlyDesired.join(', ')}
        </p>
      )}
      {instance.driftDetail.onlyRunning.length > 0 && (
        <p className="text-xs text-red-700 mt-0.5">
          Running only: {instance.driftDetail.onlyRunning.join(', ')}
        </p>
      )}
    </div>
  );
}

function RolloutCell({ instance }: { instance: FleetInstanceView }) {
  if (!instance.rolloutState && !instance.rolloutStateReason) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  return (
    <div>
      {instance.rolloutState && (
        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          {instance.rolloutState}
        </span>
      )}
      {instance.rolloutStateReason && (
        <p className="text-xs text-gray-600 mt-0.5">{instance.rolloutStateReason}</p>
      )}
    </div>
  );
}

function InstanceRow({
  instance,
  onEdit,
}: {
  instance: FleetInstanceView;
  onEdit: (instance: FleetInstanceView) => void;
}) {
  // D-08 — topmonks-internal (managed: external) renders as an inventory
  // row with NO control offered, not even a greyed-out one (P-29/P-46).
  // Every mode of ops/provision-instance.sh exits 3 on it; the endpoint
  // refuses a change to it independently of what this row renders (Plans
  // 106-09/106-10).
  if (!instance.editable) {
    return (
      <tr className="bg-gray-50 text-gray-500">
        <td className="px-3 py-2 text-sm align-top">
          <div className="font-medium">{instance.id}</div>
          <div className="text-xs">{instance.domain}</div>
          <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
            {instance.managed}
          </span>
        </td>
        <td className="px-3 py-2 text-sm align-top">{instance.wave}</td>
        <td colSpan={6} className="px-3 py-2 text-sm align-top italic">
          read-only — managed outside fleet tooling (production)
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="px-3 py-2 text-sm align-top">
        <div className="font-medium">{instance.id}</div>
        <div className="text-xs text-gray-600">{instance.domain}</div>
        <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
          {instance.managed}
        </span>
      </td>
      <td className="px-3 py-2 text-sm align-top">{instance.wave}</td>
      <td className="px-3 py-2 text-sm align-top">
        <DesiredModulesCell instance={instance} />
      </td>
      <td className="px-3 py-2 text-sm align-top">
        <RunningModulesCell instance={instance} />
      </td>
      <td className="px-3 py-2 text-sm align-top">
        <DriftCell instance={instance} />
      </td>
      <td className="px-3 py-2 text-sm align-top">
        <RolloutCell instance={instance} />
      </td>
      <td className="px-3 py-2 text-sm align-top whitespace-nowrap">
        {instance.runningCount ?? '—'} / {instance.desiredCount ?? '—'}
      </td>
      {/* Plan 106-12 — the per-row action column: an action renders ONLY
          when `instance.editable` is true, which this branch already
          guarantees (the `!instance.editable` branch above returns
          early). */}
      <td className="px-3 py-2 text-sm align-top">
        <button
          type="button"
          onClick={() => onEdit(instance)}
          className="text-xs px-2 py-1 rounded border border-primary-400 text-primary-700 hover:bg-primary-50"
        >
          Edit modules
        </button>
      </td>
    </tr>
  );
}

export default function FleetConsolePage() {
  const { data, isLoading, error } = useFleetInstances();
  const [editingInstance, setEditingInstance] = useState<FleetInstanceView | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-700">
        Failed to load fleet instances:{' '}
        {error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Fleet Console</h1>
        <p className="text-sm text-gray-600 max-w-3xl">
          This page reads <code>ops/fleet.yaml</code> from git as the desired module state and
          the live ECS task definition as the running state. A module change takes effect only
          after a redeploy completes — expect roughly two to three minutes before the Running
          column reflects a save (D-03). <code>topmonks-internal</code> is production and is not
          manageable from here (D-08).
        </p>
      </div>

      <div className="overflow-x-auto border rounded">
        <table aria-label="Fleet instances" className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Instance
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Wave</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Desired modules
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Running modules
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Drift</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Rollout
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Tasks</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data?.instances.map((instance) => (
              <InstanceRow key={instance.id} instance={instance} onEdit={setEditingInstance} />
            ))}
          </tbody>
        </table>
      </div>

      {editingInstance && data && (
        <FleetModuleEditor
          instance={editingInstance}
          baseSha={data.baseSha}
          moduleCatalog={data.moduleCatalog}
          onClose={() => setEditingInstance(null)}
        />
      )}

      <SuperadminGrantPanel />
    </div>
  );
}
